import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  GitBranch,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Activity,
  Users,
  Target,
  Eye,
  Brain,
  ArrowRight,
  Zap,
  BarChart3,
  Clock,
  RefreshCw,
  Lock,
  Server,
  Shield,
  Radio,
} from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

// ─── Hero Card ────────────────────────────────────────────────────────────────

const wait = (ms) => new Promise((res) => setTimeout(res, ms))

function HeroVisualization() {
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setPhase(0)
        await wait(600)
        if (cancelled) break
        setPhase(1)   // "your standup" row
        await wait(2400)
        if (cancelled) break
        setPhase(2)   // "rithmo-powered" row
        await wait(1800)
        if (cancelled) break
        setPhase(3)   // tagline
        await wait(4200)
        if (cancelled) break
      }
    }
    run()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="relative w-full max-w-sm mx-auto lg:mx-0">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="bg-white rounded-2xl overflow-hidden shadow-premium border border-gray-200"
      >
        {/* Before row */}
        <motion.div
          animate={{ opacity: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          className="px-8 pt-8 pb-7 border-b border-gray-100"
        >
          <p className="text-[11px] font-mono text-gray-400 uppercase tracking-widest mb-4">Your standup</p>
          <div className="flex items-baseline gap-2">
            <span className="text-7xl font-bold text-gray-200 font-mono leading-none">43</span>
            <span className="text-lg text-gray-300 font-mono">min</span>
          </div>
        </motion.div>

        {/* After row */}
        <motion.div
          animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 10 }}
          transition={{ duration: 0.55 }}
          className="px-8 pt-7 pb-8"
        >
          <p className="text-[11px] font-mono text-accent-500 uppercase tracking-widest mb-4">Rithmo powered standup</p>
          <div className="flex items-baseline gap-2">
            <span className="text-7xl font-bold text-gray-900 font-mono leading-none">9</span>
            <span className="text-lg text-gray-400 font-mono">min</span>
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.div
          animate={{ opacity: phase >= 3 ? 1 : 0 }}
          transition={{ duration: 0.7 }}
          className="px-8 py-5 border-t border-gray-100 text-center"
        >
          <p className="text-gray-400 text-[11px] uppercase tracking-widest mb-1">every morning</p>
          <p className="text-accent-500 text-sm font-medium">34 minutes back.</p>
        </motion.div>
      </motion.div>
    </div>
  )
}

// ─── FAQ Accordion ────────────────────────────────────────────────────────────

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="font-semibold text-gray-900">{question}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 ml-4"
        >
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-gray-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Page data ────────────────────────────────────────────────────────────────

const painPoints = [
  {
    icon: <Clock className="w-5 h-5" />,
    title: 'Standups That Cost More Than They Return',
    description: 'Fifteen minutes on the calendar, thirty in reality. Most of it status you already knew. Rithmo gets your team through it faster.',
  },
  {
    icon: <RefreshCw className="w-5 h-5" />,
    title: 'The Same Debate, Again',
    description: 'Last week\'s infrastructure discussion came back because nobody wrote down the decision. Rithmo captures the resolution so the conversation stays closed.',
  },
  {
    icon: <Activity className="w-5 h-5" />,
    title: 'Action Items That Disappear',
    description: 'Someone said they\'d handle it. Nobody remembers who, or what. Rithmo captures every action item with an owner before the meeting ends.',
  },
  {
    icon: <Eye className="w-5 h-5" />,
    title: 'Meetings That Eat Your Build Time',
    description: 'Engineers build best in long uninterrupted blocks. Every meeting fragments the day. Rithmo helps your team spend less time in meetings without losing the alignment they\'re meant to create.',
  },
]

const features = [
  {
    title: 'Get Through Meetings Faster',
    description: 'Rithmo tracks time against the agenda and surfaces when discussions are running long so your team spends 45 minutes building, not 45 minutes planning to build.',
    icon: <Clock className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-mono uppercase tracking-wide">Time · Sprint Ceremonies</p>
        {[
          { label: 'Daily standup', budget: '15m', used: '9m' },
          { label: 'Sprint planning', budget: '60m', used: '57m' },
          { label: 'Retrospective', budget: '45m', used: '44m' },
          { label: 'Backlog refinement', budget: '30m', used: '28m' },
        ].map(d => (
          <div key={d.label} className="flex items-center justify-between">
            <span className="text-sm text-gray-700">{d.label}</span>
            <span className="text-sm font-medium text-success">
              {d.used} <span className="text-gray-400 font-normal">/ {d.budget}</span>
            </span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-accent text-xs">All ceremonies on time this sprint</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Every Decision, Captured',
    description: 'Action items, owners, and decisions captured automatically so nothing disappears when the meeting ends. The decision was made. Rithmo makes sure everyone knows it.',
    icon: <CheckCircle className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-mono uppercase tracking-wide">Decisions · Sprint Planning</p>
        {[
          { item: 'API versioning strategy', owner: 'Alex R.' },
          { item: 'On-call rotation change', owner: 'Sam K.' },
          { item: 'Feature flag rollout plan', owner: 'Jamie L.' },
        ].map(a => (
          <div key={a.item} className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-700 flex-1">{a.item}</span>
            <span className="text-sm font-medium text-success">{a.owner}</span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-accent text-xs">8 decisions logged · all have owners</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Resolve Topics. Not Revisit Them.',
    description: 'When the same discussion comes back for the third week running, Rithmo flags it so your team can resolve it async before it eats another hour.',
    icon: <RefreshCw className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-mono uppercase tracking-wide">Recurring Topics · Last 2 Weeks</p>
        {[
          { topic: 'Infrastructure ownership', count: 3, flagged: true },
          { topic: 'API versioning approach', count: 2, flagged: true },
          { topic: 'On-call rotation structure', count: 1, flagged: false },
        ].map(r => (
          <div key={r.topic} className="flex items-start justify-between gap-4">
            <span className="text-sm text-gray-700 flex-1">{r.topic}</span>
            <span className={`text-sm font-medium ${r.flagged ? 'text-warning' : 'text-gray-400'}`}>
              {r.count}x {r.flagged ? '· flagged' : ''}
            </span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-warning text-xs">2 topics moved to async thread</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Your Update, Without the Meeting',
    description: 'Some teams use Rithmo to deliver their daily status before the standup starts. Blockers surfaced, priorities logged. The standup takes 9 minutes. You stayed in flow the whole time.',
    icon: <Zap className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3 font-mono text-xs">
        <p className="text-gray-400 uppercase tracking-wide">Daily Standup · 09:04</p>
        {[
          { label: 'Alex R.', update: 'async · delivered by Rithmo', async: true },
          { label: 'Jamie L.', update: 'async · delivered by Rithmo', async: true },
          { label: 'Sam K.', update: 'attended · 2 min', async: false },
          { label: 'Morgan T.', update: 'async · delivered by Rithmo', async: true },
        ].map(r => (
          <div key={r.label} className="flex items-center justify-between">
            <span className="text-gray-700">{r.label}</span>
            <span className={`text-[11px] ${r.async ? 'text-accent-500' : 'text-gray-400'}`}>{r.update}</span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-accent text-xs">Standup complete · 9 min · all unblocked</span>
        </div>
      </div>
    ),
  },
]

const exampleInsights = [
  { timestamp: '09:13', tag: 'DONE', text: 'Daily standup completed in 9 minutes. 4 async updates delivered before start. Team unblocked and building by 09:13.', color: 'text-success', tagBg: 'bg-green-50 border-green-200' },
  { timestamp: '10:58', tag: 'CAPTURED', text: '8 decisions and 12 action items logged during sprint planning. All items have owners. Nothing left unassigned.', color: 'text-accent-600', tagBg: 'bg-teal-50 border-teal-200' },
  { timestamp: '14:17', tag: 'FLAGGED', text: 'Infrastructure ownership topic recurring for the third week. Added to async thread for resolution before next sprint planning.', color: 'text-warning', tagBg: 'bg-yellow-50 border-yellow-200' },
  { timestamp: '15:44', tag: 'SAVED', text: 'Architecture review completed 2 minutes early. Pre-meeting brief cut discussion time in half. Brief delivered automatically after.', color: 'text-primary-600', tagBg: 'bg-primary-50 border-primary-200' },
]

const outcomes = [
  { left: 'Standups Under 10 Minutes', right: 'Decisions That Don\'t Disappear' },
  { left: 'Action Items With Owners', right: 'Fewer Repeated Discussions' },
  { left: 'Async Updates That Work', right: 'More Hours Building, Fewer in Meetings' },
]

const workflowSteps = [
  { label: 'Engineering Meeting Starts', description: 'Standup, sprint planning, arch review, retro. Whatever\'s on the calendar', icon: <Users className="w-5 h-5" /> },
  { label: 'Rithmo Is Already There', description: 'Joined before the first person spoke. No setup, no joining link, no configuration needed', icon: <Eye className="w-5 h-5" /> },
  { label: 'Updates and Decisions Captured', description: 'Action items assigned, decisions logged, blockers surfaced, automatically', icon: <Activity className="w-5 h-5" /> },
  { label: 'Brief Delivered to the Team', description: 'Everyone knows what happened without having to attend every meeting', icon: <Zap className="w-5 h-5" /> },
  { label: 'Recurring Topics Flagged', description: 'Discussions that keep coming back get surfaced for async resolution before the next meeting', icon: <RefreshCw className="w-5 h-5" /> },
  { label: 'Your Team Builds More', description: 'Less time in meetings. Same alignment. More momentum.', icon: <GitBranch className="w-5 h-5" /> },
]

const faqs = [
  {
    question: 'Does Rithmo replace our current meeting platform?',
    answer: 'No. Rithmo works alongside existing meeting platforms, including Zoom. Nothing changes about how your team runs meetings.',
  },
  {
    question: 'Can Rithmo deliver status updates on behalf of engineers?',
    answer: 'Some teams use Rithmo to deliver updates before the standup starts: blockers, progress, priorities. The standup becomes a fast sync instead of a round robin. Optional and team configured.',
  },
  {
    question: 'Does Rithmo monitor individual engineers?',
    answer: 'No. Rithmo captures what meetings produce: decisions, action items, time spent. Not who said what or how individuals performed. The output serves the team.',
  },
  {
    question: 'Is Rithmo an engineering productivity tracker?',
    answer: 'No. Rithmo focuses on what meetings produce and how long they take, not individual engineer output or performance.',
  },
  {
    question: 'Who sees the data?',
    answer: 'Meeting outputs (decisions, action items, briefs) are scoped to your organization. Rithmo is not a surveillance tool and is not designed to feed manager dashboards with individual performance data.',
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

function EngineeringPage() {
  usePageMeta({
    title: 'Rithmo for Engineering Teams: AI Meeting Host',
    description: 'Run structured standups, retros, and planning sessions with Rithmo. Agenda enforcement, action capture, and meeting quality tracking built for engineering teams.',
  })
  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/50 to-white" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-4">
                <span className="badge-primary text-sm">For Engineering Teams</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Having an AI Run Your Meetings Sounds Terrible.{' '}
                <span className="gradient-text">So Does Another 43 Minute Standup.</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
                That standup already happened this morning. It ate 40 minutes of your build window. Rithmo keeps it under 10. Decisions captured, everyone building before 9:15.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/signup" className="btn-primary text-lg px-8 py-4 text-center">
                  Get Early Access
                </Link>
              </div>
            </motion.div>

            <div className="flex justify-center lg:justify-end">
              <HeroVisualization />
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                How a 43 Minute Standup{' '}
                <span className="gradient-text">Becomes 9.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-10">
                Yes, it requires a small change to how your team runs standups. A 30 second check-in before the meeting. That is it. Rithmo handles everything else. And the math is hard to argue with.
              </p>

              <div className="space-y-8">
                {[
                  {
                    step: '01',
                    title: 'Everyone checks in before the meeting',
                    body: 'Each engineer sends Rithmo a quick update before the standup starts. What shipped, what is blocked, what needs a decision. Thirty seconds. Done.',
                  },
                  {
                    step: '02',
                    title: 'Rithmo builds the agenda',
                    body: 'All updates synthesized into a structured run of show. Blockers surfaced. Decisions flagged. No round robin. The meeting already knows what it needs to cover.',
                  },
                  {
                    step: '03',
                    title: 'Questions only. Then everyone builds.',
                    body: 'Rithmo presents the agenda, fields questions, captures decisions. If there are none, the standup is over. Nine minutes. Everyone building by 9:13.',
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-5"
                  >
                    <span className="text-2xl font-bold text-gray-100 font-mono flex-shrink-0 w-8 leading-tight">{item.step}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
              <div className="bg-white rounded-2xl overflow-hidden shadow-premium border border-gray-200">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3 border-b border-gray-200">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                  </div>
                  <span className="text-xs text-gray-400 font-mono">rithmo · standup check-in</span>
                </div>

                <div className="p-5 space-y-4 font-mono text-xs">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest">Before standup · 08:51</p>

                  {/* Engineer messages */}
                  {[
                    { name: 'Alex R.', msg: 'Auth fix shipped. Blocked on design handoff for the onboarding flow.' },
                    { name: 'Jamie L.', msg: 'API versioning done. Need a call on the rollout strategy before Thursday.' },
                    { name: 'Sam K.', msg: 'CI pipeline still flaking on the integration suite. Morgan is looking at it.' },
                    { name: 'Morgan T.', msg: 'On the CI issue. Should have a fix by EOD. Nothing else blocking.' },
                  ].map((msg, i) => (
                    <motion.div
                      key={msg.name}
                      initial={{ opacity: 0, y: 6 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + i * 0.15 }}
                      className="flex gap-3"
                    >
                      <span className="text-accent-500 font-bold flex-shrink-0">{msg.name}</span>
                      <span className="text-gray-600">{msg.msg}</span>
                    </motion.div>
                  ))}

                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.9 }}
                    className="border-t border-gray-100 pt-4 space-y-2"
                  >
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-3">Rithmo · Agenda built · 09:04</p>
                    {[
                      { tag: 'DECISION', color: 'text-warning', msg: 'API rollout strategy · Jamie + team' },
                      { tag: 'BLOCKER',  color: 'text-error',   msg: 'Design handoff · Alex · unblocked by noon?' },
                      { tag: 'WATCH',    color: 'text-gray-400', msg: 'CI pipeline · Morgan tracking · EOD' },
                    ].map((item, i) => (
                      <motion.div
                        key={item.tag}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 1.0 + i * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <span className={`flex-shrink-0 font-bold ${item.color}`}>[{item.tag}]</span>
                        <span className="text-gray-600">{item.msg}</span>
                      </motion.div>
                    ))}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.35 }}
                    className="flex items-center justify-between bg-success/10 border border-success/20 rounded-xl px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-success flex items-center justify-center">
                        <svg width="8" height="7" viewBox="0 0 8 7" fill="none"><path d="M1 3.5l2 2L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      <span className="text-success text-xs font-semibold">Standup complete · 9 min</span>
                    </div>
                    <span className="text-gray-400 text-[10px]">everyone building by 09:13</span>
                  </motion.div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── What Rithmo Does ──────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-primary-50/30 to-white" />
        <div className="absolute -top-20 right-10 w-72 h-72 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-10 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Built for Engineering Meetings.
              <br />
              <span className="gradient-text">Not Meeting Culture.</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Rithmo handles the operational layer of your meetings: time, decisions, action items, recurring topics. So your team can focus on the actual work.
            </p>
          </motion.div>

          <div className="space-y-20">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-glow-sm text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
                <div className={i % 2 === 1 ? 'lg:col-start-1' : ''}>{feature.visual}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Technical Integration ────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute -top-20 right-0 w-96 h-96 bg-accent-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-500/8 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left: copy */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-4">Under the Hood</span>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Set Up in Minutes.{' '}
                <span className="gradient-text">Nothing to Maintain.</span>
              </h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Rithmo is a pure API integration. No agents to install, no infrastructure to manage, no changes to how your team runs meetings. It was designed to be the kind of tool an engineer would actually want to evaluate.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: <Lock className="w-5 h-5" />,
                    title: 'One Time Zoom OAuth',
                    body: 'Connect your Zoom workspace once via standard OAuth 2.0. No API keys to rotate, no webhooks to configure, revocable from your Zoom admin dashboard at any time.',
                  },
                  {
                    icon: <Radio className="w-5 h-5" />,
                    title: 'Dual Bot Architecture',
                    body: 'Rithmo deploys as two distinct bot presences per meeting: a named participant and a silent observer. This ensures complete signal coverage regardless of host settings or meeting configuration.',
                  },
                  {
                    icon: <Server className="w-5 h-5" />,
                    title: 'Zero Infrastructure Overhead',
                    body: 'Nothing to deploy on your end. Rithmo handles all compute, processing, and signal extraction on its own infrastructure. Your team installs nothing.',
                  },
                  {
                    icon: <Shield className="w-5 h-5" />,
                    title: 'Scoped Data Processing',
                    body: 'Audio is processed in-flight for behavioral signals. Raw recordings are not retained. Transcripts are used for pattern analysis only, scoped to your organization.',
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0 text-white shadow-glow-sm">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{item.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: terminal card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl overflow-hidden shadow-premium"
            >
              {/* Terminal header */}
              <div className="bg-dark-500 px-4 py-3 flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-error" />
                  <div className="w-2.5 h-2.5 rounded-full bg-warning" />
                  <div className="w-2.5 h-2.5 rounded-full bg-success" />
                </div>
                <span className="text-xs text-dark-200 font-mono">rithmo · integration · status</span>
              </div>

              <div className="p-6 font-mono text-sm space-y-6">
                {/* Status checks */}
                <div className="space-y-2.5">
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Connection Status</p>
                  {[
                    { label: 'Zoom OAuth', status: 'authorized', ok: true },
                    { label: 'Bot deployment', status: 'active (x2)', ok: true },
                    { label: 'Meeting detection', status: 'listening', ok: true },
                    { label: 'Intelligence pipeline', status: 'processing', ok: true },
                  ].map((row, i) => (
                    <motion.div
                      key={row.label}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.12 }}
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 border border-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-success text-base leading-none">●</span>
                        <span className="text-gray-700 text-xs">{row.label}</span>
                      </div>
                      <span className="text-xs text-accent-600 font-medium">{row.status}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Architecture note */}
                <div className="border-t border-gray-100 pt-5 space-y-2">
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Integration Architecture</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-2 py-1 rounded bg-primary-50 text-primary-700 font-medium">Zoom API</span>
                    <ArrowRight className="w-3 h-3 text-gray-300" />
                    <span className="px-2 py-1 rounded bg-primary-50 text-primary-700 font-medium">OAuth 2.0</span>
                    <ArrowRight className="w-3 h-3 text-gray-300" />
                    <span className="px-2 py-1 rounded bg-accent-50 text-accent-700 font-medium">Rithmo</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                    <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 font-medium">Bot A</span>
                    <span className="text-gray-300">+</span>
                    <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 font-medium">Bot B (silent)</span>
                    <ArrowRight className="w-3 h-3 text-gray-300" />
                    <span className="px-2 py-1 rounded bg-accent-50 text-accent-700 font-medium">Signal Engine</span>
                  </div>
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.0 }}
                  className="text-xs text-gray-400 border-t border-gray-100 pt-4"
                >
                  avg setup time: &lt; 1 minute · no code changes required
                </motion.p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ background: 'radial-gradient(ellipse at 25% 50%, rgba(11,111,164,0.35) 0%, transparent 60%)' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left: copy + checklist */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-400 mb-4">Security and Privacy</span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Minimal Footprint.{' '}
                <span className="gradient-text">Scoped Access.</span>
              </h2>
              <p className="text-lg text-dark-100 mb-10 leading-relaxed">
                Rithmo was built to pass security review, not trigger it. The integration surface is intentionally small: OAuth only, no persistent credentials, no raw recording retention.
              </p>

              <div className="space-y-5">
                {[
                  { title: 'TLS 1.3 in transit, AES-256 at rest', body: 'All data in motion and at rest is encrypted using current industry standards.' },
                  { title: 'Minimum OAuth scopes', body: 'Rithmo requests only the Zoom permissions required to join and observe meetings. The full scope list is visible at authorization time.' },
                  { title: 'No audio or video retention', body: 'Audio is transcribed and processed in-flight. Raw recordings are never written to disk.' },
                  { title: 'No model training on org data', body: 'Meeting content is never used to train models. Intelligence is derived from behavioral signals scoped to your organization.' },
                  { title: 'Fully org-isolated', body: 'All data is isolated to your organization. No cross-tenant access is possible by design.' },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium text-sm">{item.title}</p>
                      <p className="text-dark-200 text-sm mt-0.5 leading-relaxed">{item.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: OAuth scope + data lifecycle card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
            >
              <div className="bg-dark-500/60 px-4 py-3 flex items-center gap-3 border-b border-white/10">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-error" />
                  <div className="w-2.5 h-2.5 rounded-full bg-warning" />
                  <div className="w-2.5 h-2.5 rounded-full bg-success" />
                </div>
                <span className="text-xs text-dark-200 font-mono">rithmo · oauth · requested-scopes</span>
              </div>

              <div className="p-6 font-mono text-sm space-y-6">
                <div className="space-y-2">
                  <p className="text-xs text-dark-300 uppercase tracking-widest mb-3">Zoom OAuth Scopes Requested</p>
                  {[
                    { scope: 'meeting:read', reason: 'detect scheduled meetings' },
                    { scope: 'meeting:write', reason: 'join as bot participant' },
                    { scope: 'user:read', reason: 'identify participants' },
                    { scope: 'recording:read', reason: 'access in-meeting audio stream' },
                  ].map((s, i) => (
                    <motion.div
                      key={s.scope}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 border border-white/8"
                    >
                      <span className="text-accent-400 text-xs">{s.scope}</span>
                      <span className="text-dark-300 text-xs">{s.reason}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="border-t border-white/10 pt-5">
                  <p className="text-xs text-dark-300 uppercase tracking-widest mb-3">Data Lifecycle</p>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Audio stream', value: 'transcribed → discarded' },
                      { label: 'Transcript', value: 'analyzed → not stored long-term' },
                      { label: 'Behavioral signals', value: 'aggregated per org' },
                      { label: 'Raw recordings', value: 'never written to disk' },
                    ].map((d, i) => (
                      <motion.div
                        key={d.label}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.9 + i * 0.08 }}
                        className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-white/3"
                      >
                        <span className="text-dark-200 text-xs">{d.label}</span>
                        <span className="text-xs text-accent-400 font-medium">{d.value}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.2 }}
                  className="text-xs text-dark-400 border-t border-white/10 pt-4"
                >
                  access revocable at any time from Zoom admin dashboard
                </motion.p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── Future Vision ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 60% 50%, rgba(22,193,178,0.15) 0%, transparent 60%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mx-auto mb-6 shadow-glow">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              The Best Standup Is the One{' '}
              <span className="gradient-text">That's Already Done.</span>
            </h2>
            <p className="text-lg text-dark-100 leading-relaxed">
              Your update delivered before the standup starts. Blockers surfaced before anyone asks. Nine minutes because Rithmo handled the rest. Engineering teams are already running it this way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(22,193,178,0.15) 0%, transparent 60%)' }} />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Your Engineering Team Deserves Better Meetings.
            </h2>
            <p className="text-lg text-dark-100 mb-10 max-w-xl mx-auto">
              Rithmo is working with early engineering teams who want shorter standups, captured decisions, and more time to build. Join them.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup" className="btn-primary text-lg px-8 py-4">
                Get Early Access
              </Link>
              <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                Talk To The Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card divide-y divide-gray-100"
          >
            {faqs.map(faq => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default EngineeringPage
